import axios from 'axios';

// URL do backend - sempre usar a variável de ambiente
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
console.log('🌐 Backend URL configurada:', BACKEND_URL);
console.log('🔧 ENV REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);

class ApiService {
  constructor() {
    console.log('📡 Inicializando ApiService com baseURL:', `${BACKEND_URL}/api`);
    this.api = axios.create({
      baseURL: `${BACKEND_URL}/api`,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 60000,
      withCredentials: false,
    });

    // Interceptor para adicionar token automaticamente
    this.api.interceptors.request.use(
      (config) => {
        // Adicionar token do localStorage automaticamente
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        console.log('🔗 API Request:', config.method?.toUpperCase(), config.url, 'Base:', config.baseURL, 'Auth:', !!token);
        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response) => {
        console.log('✅ API Response:', response.status, response.config.url);
        return response;
      },
      (error) => {
        console.error('❌ Response Error:', error.response?.status, error.config?.url, error.message);
        // Auth expirada / revogada (ex: super_admin clicou em "Desconectar Todos")
        // → limpar credenciais e redirecionar para login
        if (error.response?.status === 401) {
          const isLoginRequest = (error.config?.url || '').includes('/auth/login');
          if (!isLoginRequest && typeof window !== 'undefined') {
            try {
              localStorage.removeItem('authToken');
              localStorage.removeItem('user');
              delete this.api.defaults.headers.common['Authorization'];
            } catch (e) {
              console.warn('Falha ao limpar credenciais locais:', e);
            }
            if (!window.location.pathname.endsWith('/admin/login')) {
              window.location.href = '/admin/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Configurar token de autenticação
  setAuthToken(token) {
    if (token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  // DASHBOARD
  async getDashboardResumo() {
    const response = await this.api.get('/dashboard/resumo');
    return response.data;
  }

  async getAlertasUrgentes() {
    const response = await this.api.get('/dashboard/alertas');
    return response.data;
  }

  // MOTOS
  async getMotos() {
    const response = await this.api.get('/motos');
    return response.data;
  }

  async getMoto(id) {
    const response = await this.api.get(`/motos/${id}`);
    return response.data;
  }

  async criarMoto(moto) {
    const response = await this.api.post('/motos', moto);
    return response.data;
  }

  async atualizarMoto(id, dados) {
    const response = await this.api.put(`/motos/${id}`, dados);
    return response.data;
  }

  async deletarMoto(id) {
    const response = await this.api.delete(`/motos/${id}`);
    return response.data;
  }

  async getAlertasManutencao() {
    const response = await this.api.get('/motos/alertas/manutencao');
    return response.data;
  }

  // CLIENTES
  async getClientes() {
    const response = await this.api.get('/clientes');
    return response.data;
  }

  async getCliente(id) {
    const response = await this.api.get(`/clientes/${id}`);
    return response.data;
  }

  async criarCliente(cliente) {
    const response = await this.api.post('/clientes', cliente);
    return response.data;
  }

  async extractClienteFromDocuments(formData) {
    const response = await this.api.post('/clientes/extract-from-documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async extractClienteFromContractOnly(formData) {
    const response = await this.api.post('/clientes/extract-from-contract', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async atualizarCliente(id, dados) {
    const response = await this.api.put(`/clientes/${id}`, dados);
    return response.data;
  }

  async deletarCliente(id) {
    const response = await this.api.delete(`/clientes/${id}`);
    return response.data;
  }

  // LOCAÇÕES
  async getLocacoes() {
    const response = await this.api.get('/locacoes');
    return response.data;
  }

  async criarLocacao(locacao) {
    const response = await this.api.post('/locacoes', locacao);
    return response.data;
  }

  async atualizarLocacao(id, locacao) {
    const response = await this.api.put(`/locacoes/${id}`, locacao);
    return response.data;
  }

  async deletarLocacao(id) {
    const response = await this.api.delete(`/locacoes/${id}`);
    return response.data;
  }

  async finalizarLocacao(id, kmFinal) {
    const response = await this.api.put(`/locacoes/${id}/finalizar?km_final=${kmFinal}`);
    return response.data;
  }

  // MANUTENÇÕES
  async getManutencoes(motoId = null) {
    const url = motoId ? `/manutencoes?moto_id=${motoId}` : '/manutencoes';
    const response = await this.api.get(url);
    return response.data;
  }

  async registrarManutencao(manutencao) {
    const response = await this.api.post('/manutencoes', manutencao);
    return response.data;
  }

  async atualizarManutencao(id, manutencao) {
    const response = await this.api.put(`/manutencoes/${id}`, manutencao);
    return response.data;
  }

  async deletarManutencao(id) {
    const response = await this.api.delete(`/manutencoes/${id}`);
    return response.data;
  }

  // Registrar manutenção avulsa (NÃO afeta ciclo de óleo)
  async registrarManutencaoAvulsa(dados) {
    const response = await this.api.post('/manutencoes/avulsa', dados);
    return response.data;
  }

  // BRASIL SAT - RASTREAMENTO E MANUTENÇÃO
  async configurarBrasilSAT(username, password) {
    const response = await this.api.post('/brasil-sat/configurar', {
      username,
      password
    });
    return response.data;
  }

  async testarBrasilSAT(username = null, password = null) {
    const response = await this.api.post('/brasil-sat/testar', {
      username,
      password
    });
    return response.data;
  }

  async atualizarQuilometragemMotos() {
    const response = await this.api.post('/brasil-sat/atualizar-quilometragem');
    return response.data;
  }

  async getAlertasManutencaoBrasilSAT() {
    const response = await this.api.get('/brasil-sat/alertas-manutencao');
    return response.data;
  }

  async enviarAlertaWhatsApp(alertaId, templatePersonalizado = null) {
    const response = await this.api.post('/brasil-sat/enviar-alerta-whatsapp', {
      alerta_id: alertaId,
      template_personalizado: templatePersonalizado
    });
    return response.data;
  }

  async ignorarAlerta(alertaId, observacoes = '') {
    const response = await this.api.post(`/brasil-sat/ignorar-alerta/${alertaId}`, null, {
      params: { observacoes }
    });
    return response.data;
  }

  async getHistoricoQuilometragem(motoId, limite = 30) {
    const response = await this.api.get(`/brasil-sat/historico-quilometragem/${motoId}`, {
      params: { limite }
    });
    return response.data;
  }

  async getEstatisticasBrasilSAT() {
    const response = await this.api.get('/brasil-sat/estatisticas');
    return response.data;
  }

  async getStatusBrasilSAT() {
    const response = await this.api.get('/brasil-sat/status');
    return response.data;
  }

  async atualizarQuilometragemManual(motoId, novaQuilometragem) {
    const response = await this.api.post(`/brasil-sat/atualizar-quilometragem-manual?moto_id=${motoId}&nova_quilometragem=${novaQuilometragem}`);
    return response.data;
  }

  // PAGAMENTOS
  async getPagamentos(locacaoId = null) {
    const url = locacaoId ? `/pagamentos?locacao_id=${locacaoId}` : '/pagamentos';
    const response = await this.api.get(url);
    return response.data;
  }

  async marcarPagamentoPago(id) {
    const response = await this.api.put(`/pagamentos/${id}/pagar`);
    return response.data;
  }

  async marcarPagamentoPagoComJuros(id, dados) {
    const response = await this.api.put(`/pagamentos/${id}/pagar-com-juros`, dados);
    return response.data;
  }

  async getPagamentosAtrasados() {
    const response = await this.api.get('/pagamentos/atrasados');
    return response.data;
  }

  async calcularValoresAtraso(pagamentoId) {
    const response = await this.api.get(`/pagamentos/${pagamentoId}/calcular-atraso`);
    return response.data;
  }

  async enviarLembreteAtraso(pagamentoId) {
    const response = await this.api.post(`/pagamentos/${pagamentoId}/enviar-lembrete-atraso`);
    return response.data;
  }

  // COBRANÇAS EXTRAS
  async getCobrancasExtras() {
    const response = await this.api.get('/cobrancas-extras');
    return response.data;
  }

  async criarCobrancaExtra(cobranca) {
    const response = await this.api.post('/cobrancas-extras', cobranca);
    return response.data;
  }

  async atualizarCobrancaExtra(id, dados) {
    const response = await this.api.put(`/cobrancas-extras/${id}`, dados);
    return response.data;
  }

  // Alias para consistência com a interface
  async atualizarCobranca(id, dados) {
    return this.atualizarCobrancaExtra(id, dados);
  }

  async deletarCobrancaExtra(id) {
    const response = await this.api.delete(`/cobrancas-extras/${id}`);
    return response.data;
  }

  // Registrar abatimento parcial em um débito
  async registrarAbatimento(cobrancaId, dados) {
    const response = await this.api.post(`/cobrancas-extras/${cobrancaId}/abatimento`, dados);
    return response.data;
  }

  // Abater valor do total de débitos do cliente
  async abaterTotalDebitos(dados) {
    const response = await this.api.post('/cobrancas-extras/abatimento-total', dados);
    return response.data;
  }

  // Atualizar configuração de parcelamento
  async atualizarParcelamento(locacaoId, dados) {
    const response = await this.api.post(`/locacoes/${locacaoId}/parcelamento`, dados);
    return response.data;
  }

  // Alias para consistência com a interface
  async deletarCobranca(id) {
    return this.deletarCobrancaExtra(id);
  }

  async getCobrancasPendentes() {
    const response = await this.api.get('/cobrancas-extras/pendentes/lista');
    return response.data;
  }

  async getCobrancasPorLocacao(locacaoId) {
    const response = await this.api.get(`/cobrancas-extras/locacao/${locacaoId}`);
    return response.data;
  }

  async getTemplatesCobranca() {
    const response = await this.api.get('/cobrancas-extras/templates/lista');
    return response.data;
  }

  // ===== SISTEMA E CONFIGURAÇÕES =====
  async getStatusSistema() {
    const response = await this.api.get('/sistema/status');
    return response.data;
  }

  async getConfigDetran() {
    const response = await this.api.get('/sistema/config-detran');
    return response.data;
  }

  async updateConfigDetran(config) {
    const response = await this.api.put('/sistema/config-detran', config);
    return response.data;
  }

  async executarConsultaDetranAgora() {
    const response = await this.api.post('/sistema/executar-consulta-detran');
    return response.data;
  }

  // ===== AUTENTICAÇÃO =====
  async login(username, password) {
    const response = await this.api.post('/auth/login', {
      username,
      senha: password
    });
    return response.data;
  }

  async getCurrentUser() {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  async logout() {
    const response = await this.api.post('/auth/logout');
    return response.data;
  }

  // ===== PESQUISA GLOBAL =====
  async pesquisaGlobal(termo) {
    const response = await this.api.get(`/sistema/pesquisa-global?q=${encodeURIComponent(termo)}`);
    return response.data;
  }

  // ===== SISTEMA DE BLOQUEIO =====
  async bloquearCliente(clienteId, dados) {
    const response = await this.api.post(`/clientes/${clienteId}/bloquear`, dados);
    return response.data;
  }

  async desbloquearCliente(clienteId, dados) {
    const response = await this.api.post(`/clientes/${clienteId}/desbloquear`, dados);
    return response.data;
  }

  async verificarRestricoesCliente(clienteId) {
    const response = await this.api.get(`/clientes/${clienteId}/restricoes`);
    return response.data;
  }

  async autorizarLocacaoEspecial(clienteId, autorizacao) {
    const response = await this.api.post(`/clientes/${clienteId}/autorizar-locacao`, autorizacao);
    return response.data;
  }

  async getHistoricoBloqueios(clienteId) {
    const response = await this.api.get(`/clientes/${clienteId}/historico-bloqueios`);
    return response.data;
  }

  async getClientesBloqueados() {
    const response = await this.api.get('/clientes/bloqueados');
    return response.data;
  }

  // ===== RELATÓRIOS E HISTÓRICOS =====
  async getLocacoesPorCliente(clienteId) {
    const response = await this.api.get(`/locacoes/cliente/${clienteId}`);
    return response.data;
  }

  async getPagamentosPorCliente(clienteId) {
    const response = await this.api.get(`/pagamentos/cliente/${clienteId}`);
    return response.data;
  }

  async getCobrancasPorCliente(clienteId) {
    const response = await this.api.get(`/cobrancas-extras/cliente/${clienteId}`);
    return response.data;
  }

  // ===== CONTROLE DE STATUS DE LOCAÇÃO =====
  async pausarLocacao(locacaoId, motivo, observacoes = null) {
    const response = await this.api.post(`/locacoes/${locacaoId}/pausar`, {
      acao: 'pausar',
      motivo,
      observacoes
    });
    return response.data;
  }

  async despausarLocacao(locacaoId, motivo, observacoes = null) {
    const response = await this.api.post(`/locacoes/${locacaoId}/despausar`, {
      acao: 'despausar',
      motivo,
      observacoes
    });
    return response.data;
  }

  async desabilitarLocacao(locacaoId, motivo, observacoes = null) {
    const response = await this.api.post(`/locacoes/${locacaoId}/desabilitar`, {
      acao: 'desabilitar',
      motivo,
      observacoes
    });
    return response.data;
  }

  async reabilitarLocacao(locacaoId, motivo, observacoes = null) {
    const response = await this.api.post(`/locacoes/${locacaoId}/reabilitar`, {
      acao: 'reabilitar',
      motivo,
      observacoes
    });
    return response.data;
  }

  async cancelarLocacao(locacaoId, motivo, observacoes = null) {
    const response = await this.api.post(`/locacoes/${locacaoId}/cancelar`, {
      acao: 'cancelar',
      motivo,
      observacoes
    });
    return response.data;
  }

  async getHistoricoLocacao(locacaoId) {
    const response = await this.api.get(`/locacoes/${locacaoId}/historico`);
    return response.data;
  }

  // ===== WHATSAPP =====
  async testarWhatsApp() {
    const response = await this.api.post('/whatsapp/teste');
    return response.data;
  }

  async enviarCobrancaWhatsApp(dados) {
    const response = await this.api.post('/whatsapp/enviar-cobranca', dados);
    return response.data;
  }

  // ===== SISTEMA DE ARQUIVOS =====
  async uploadArquivoCliente(clienteId, file, tipoDocumento, descricao = '') {
    const formData = new FormData();
    formData.append('arquivo', file);  // Backend espera 'arquivo'
    formData.append('tipo', tipoDocumento);  // Backend espera 'tipo'
    formData.append('descricao', descricao);

    const response = await this.api.post(`/clientes/${clienteId}/arquivos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadArquivoMoto(motoId, file, tipoDocumento, descricao = '') {
    const formData = new FormData();
    formData.append('arquivo', file);  // Backend espera 'arquivo'
    formData.append('tipo', tipoDocumento);  // Backend espera 'tipo'
    formData.append('descricao', descricao);

    const response = await this.api.post(`/motos/${motoId}/arquivos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getArquivosCliente(clienteId) {
    const response = await this.api.get(`/clientes/${clienteId}/arquivos`);
    return response.data;
  }

  async getArquivosMoto(motoId) {
    const response = await this.api.get(`/motos/${motoId}/arquivos`);
    return response.data;
  }

  async excluirArquivoCliente(clienteId, arquivoId) {
    const response = await this.api.delete(`/arquivo/cliente/${clienteId}/${arquivoId}`);
    return response.data;
  }

  async excluirArquivoMoto(motoId, arquivoId) {
    const response = await this.api.delete(`/arquivo/moto/${motoId}/${arquivoId}`);
    return response.data;
  }

  getUrlDownloadCliente(clienteId, arquivoId) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/download/cliente/${clienteId}/${arquivoId}`;
  }

  getUrlDownloadMoto(motoId, arquivoId) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/download/moto/${motoId}/${arquivoId}`;
  }

  // ===== CONTROLE DE WHATSAPP =====
  async configurarWhatsAppApiKey(apiKey) {
    const response = await this.api.post('/whatsapp/configurar-api-key', {
      api_key: apiKey
    });
    return response.data;
  }

  async getStatusWhatsAppApi() {
    const response = await this.api.get('/whatsapp/status-api');
    return response.data;
  }

  async testarEnvioRealWhatsApp(numero) {
    const response = await this.api.post('/whatsapp/testar-envio-real', {
      numero: numero
    });
    return response.data;
  }

  async enviarLembreteManutencao(motoId, proximaManutencaoKm) {
    const response = await this.api.post('/whatsapp/enviar-lembrete-manutencao', {
      moto_id: motoId,
      proxima_manutencao_km: proximaManutencaoKm
    });
    return response.data;
  }

  async enviarAvisoBloqueio(motoId) {
    const response = await this.api.post('/whatsapp/enviar-aviso-bloqueio', {
      moto_id: motoId
    });
    return response.data;
  }

  // ===== UPLOAD DE ARQUIVOS =====
  async uploadArquivoCliente(clienteId, formData) {
    const response = await this.api.post(`/clientes/${clienteId}/arquivos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  // ===== MOTOS PÚBLICAS =====
  async getMotosPublicas() {
    const response = await this.api.get('/motos/publico');
    return response.data;
  }

  // ===== CONFIGURAÇÕES AVANÇADAS =====
  async getStatusConfiguracoes() {
    const response = await this.api.get('/configuracoes/status');
    return response.data;
  }

  async configurarSendGrid(apiKey) {
    const response = await this.api.post('/configuracoes/email/sendgrid', {
      api_key: apiKey
    });
    return response.data;
  }

  async configurarSMTP(config) {
    const response = await this.api.post('/configuracoes/email/smtp', config);
    return response.data;
  }

  async configurarUltramsg(token, instance) {
    const response = await this.api.post('/configuracoes/whatsapp/ultramsg', {
      token: token,
      instance: instance
    });
    return response.data;
  }

  async configurarMercadoPago(accessToken) {
    const response = await this.api.post('/configuracoes/pagamentos/mercadopago', {
      access_token: accessToken
    });
    return response.data;
  }

  async configurarStripe(secretKey) {
    const response = await this.api.post('/configuracoes/pagamentos/stripe', {
      secret_key: secretKey
    });
    return response.data;
  }

  async configurarDetranOficial(apiKey, endpoint) {
    const response = await this.api.post('/configuracoes/detran/oficial', {
      api_key: apiKey,
      endpoint: endpoint
    });
    return response.data;
  }

  // ===== PAGAMENTOS REAIS =====
  async gerarPixPagamento(valor, descricao, clienteInfo) {
    const response = await this.api.post('/pagamentos/gerar-pix', {
      valor: valor,
      descricao: descricao,
      cliente_info: clienteInfo
    });
    return response.data;
  }

  // ===== GERENCIAMENTO DE PAGAMENTOS =====
  async criarPagamento(pagamentoData) {
    const response = await this.api.post('/pagamentos', pagamentoData);
    return response.data;
  }

  async atualizarPagamento(pagamentoId, pagamentoData) {
    const response = await this.api.put(`/pagamentos/${pagamentoId}`, pagamentoData);
    return response.data;
  }

  async excluirPagamento(pagamentoId) {
    const response = await this.api.delete(`/pagamentos/${pagamentoId}`);
    return response.data;
  }

  async getPagamentosDetalhados() {
    const response = await this.api.get('/pagamentos/detalhados');
    return response.data;
  }

  async exportarPagamentos(filtros = {}) {
    const params = new URLSearchParams();
    
    if (filtros.status && filtros.status !== 'todos') {
      params.append('status', filtros.status);
    }
    if (filtros.data_inicio) {
      params.append('data_inicio', filtros.data_inicio);
    }
    if (filtros.data_fim) {
      params.append('data_fim', filtros.data_fim);
    }
    
    const response = await this.api.get(`/pagamentos/exportar?${params.toString()}`, {
      responseType: 'blob'
    });
    
    // Criar blob e download
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Extrair nome do arquivo do header se disponível, senão usar nome padrão
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'pagamentos_desloca.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/"/g, '');
      }
    }
    
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { success: true, filename };
  }
  // ===== WHATSAPP CONTROLE COMPLETO =====
  async getHistoricoWhatsApp() {
    const response = await this.api.get('/whatsapp/historico');
    return response.data;
  }

  async getMensagensPendentes() {
    const response = await this.api.get('/whatsapp/mensagens-pendentes');
    return response.data;
  }

  async getEstatisticasWhatsApp() {
    const response = await this.api.get('/whatsapp/estatisticas');
    return response.data;
  }

  async enviarMensagemManual(dados) {
    const response = await this.api.post('/whatsapp/enviar-manual', dados);
    return response.data;
  }

  async updateTemplateCobranca(template) {
    const response = await this.api.put('/whatsapp/template-cobranca', template);
    return response.data;
  }

  // ===== ARQUIVOS - MOTOS =====
  // Função removida - duplicada acima

  // Função removida - duplicada acima

  // ===== ARQUIVOS - MANUTENÇÕES =====
  async getArquivosManutencao(manutencaoId) {
    const response = await this.api.get(`/manutencoes/${manutencaoId}/arquivos`);
    return response.data;
  }

  async uploadArquivoManutencao(manutencaoId, formData) {
    const response = await this.api.post(`/manutencoes/${manutencaoId}/arquivos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // ===== FOTO DE PERFIL =====
  async setProfilePicture(entityType, entityId, arquivoId) {
    const response = await this.api.post(`/${entityType}s/${entityId}/perfil`, {
      arquivo_id: arquivoId
    });
    return response.data;
  }

  async downloadArquivo(arquivoId) {
    const response = await this.api.get(`/arquivos/${arquivoId}`, {
      responseType: 'blob'  // Para download de arquivo
    });
    return response;
  }

  async deleteArquivo(arquivoId) {
    const response = await this.api.delete(`/arquivos/${arquivoId}`);
    return response.data;
  }

  async atualizarArquivoMoto(id, dados) {
    const response = await this.api.patch(`/arquivos/${id}`, dados);
    return response.data;
  }

  // ===== MANUTENÇÕES COMPLETAS =====
  async createManutencao(manutencaoData) {
    const response = await this.api.post('/manutencoes', manutencaoData);
    return response.data;
  }

  async getManutencoesMoto(motoId) {
    const response = await this.api.get(`/motos/${motoId}/manutencoes`);
    return response.data;
  }

  async updateManutencao(manutencaoId, manutencaoData) {
    const response = await this.api.put(`/manutencoes/${manutencaoId}`, manutencaoData);
    return response.data;
  }

  async deleteManutencao(manutencaoId) {
    const response = await this.api.delete(`/manutencoes/${manutencaoId}`);
    return response.data;
  }

  // ===== COBRANÇAS EXTRAS =====
  async createCobrancaExtra(cobrancaData) {
    const response = await this.api.post('/cobrancas-extras', cobrancaData);
    return response.data;
  }

  async getCobrancasExtras() {
    const response = await this.api.get('/cobrancas-extras');
    return response.data;
  }

  async updateCobrancaExtra(cobrancaId, cobrancaData) {
    const response = await this.api.put(`/cobrancas-extras/${cobrancaId}`, cobrancaData);
    return response.data;
  }

  async deleteCobrancaExtra(cobrancaId) {
    const response = await this.api.delete(`/cobrancas-extras/${cobrancaId}`);
    return response.data;
  }

  // ===== RELATÓRIOS DE RECEITA =====
  async getRelatorioReceita(ano, mes) {
    const response = await this.api.get(`/relatorios/receita`, {
      params: { ano, mes }
    });
    return response.data;
  }

  async getRelatorioReceitaAnual(ano) {
    const response = await this.api.get(`/relatorios/receita/anual`, {
      params: { ano }
    });
    return response.data;
  }

  // ===== MARCAR PAGAMENTO COMO PAGO =====
  async marcarPagamentoPago(pagamentoId, dataPagamento, observacoes) {
    const response = await this.api.patch(`/pagamentos/${pagamentoId}/marcar-pago`, {
      data_pagamento: dataPagamento,
      observacoes: observacoes
    });
    return response.data;
  }

  // ===== LIMPAR PAGAMENTOS DE TESTE =====
  async limparPagamentosTeste() {
    const response = await this.api.post('/pagamentos/limpar-teste');
    return response.data;
  }

  // ===== RECEITAS =====
  async getReceitas(filtros = {}) {
    const params = new URLSearchParams();
    
    if (filtros.dataInicio) params.append('data_inicio', filtros.dataInicio);
    if (filtros.dataFim) params.append('data_fim', filtros.dataFim);
    if (filtros.tipoRecebimento) params.append('tipo_recebimento', filtros.tipoRecebimento);
    if (filtros.locacaoId) params.append('locacao_id', filtros.locacaoId);
    
    const queryString = params.toString();
    const url = queryString ? `/receitas?${queryString}` : '/receitas';
    
    const response = await this.api.get(url);
    return response.data;
  }

  async createReceita(receita) {
    const response = await this.api.post('/receitas', receita);
    return response.data;
  }

  async getReceita(id) {
    const response = await this.api.get(`/receitas/${id}`);
    return response.data;
  }

  async updateReceita(id, receita) {
    const response = await this.api.put(`/receitas/${id}`, receita);
    return response.data;
  }

  async deleteReceita(id) {
    const response = await this.api.delete(`/receitas/${id}`);
    return response.data;
  }

  async getReceitasPeriodo(dataInicio, dataFim) {
    const response = await this.api.get(`/receitas/periodo/${dataInicio}/${dataFim}`);
    return response.data;
  }

  async getTotalReceitasMes() {
    const response = await this.api.get('/receitas/total/mes-atual');
    return response.data;
  }

  // ===== ANALYTICS =====
  async trackVisit(pagina = "home") {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/track-visit`, { page: pagina });
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar visita:', error);
      return { success: false };
    }
  }

  async getVisitorStats() {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/visitor-stats`);
      return response.data;
    } catch (error) {
      console.error('Erro ao obter estatísticas:', error);
      return {
        visitantes_hoje: 0,
        visitantes_semana: 0,
        total_visitantes: 0,
        timestamp: new Date().toISOString()
      };
    }
  }

  // ===== RESERVAS =====
  async cadastrarReserva(dados) {
    try {
      const response = await this.api.post('/reservas', dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao cadastrar reserva:', error);
      throw error;
    }
  }

  async getReservas() {
    try {
      const response = await this.api.get('/reservas');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter reservas:', error);
      throw error;
    }
  }

  async atualizarReserva(id, dados) {
    try {
      const response = await this.api.put(`/reservas/${id}`, dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar reserva:', error);
      throw error;
    }
  }

  async deletarReserva(id) {
    try {
      const response = await this.api.delete(`/reservas/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao deletar reserva:', error);
      throw error;
    }
  }

  // Brasil Sat GPS
  async getBrasilSatStatus() {
    try {
      const response = await this.api.get('/admin/brasilsat/status');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter status Brasil Sat:', error);
      throw error;
    }
  }

  // Recalcular km_ultima_manutencao de todas as motos
  async recalcularKmManutencao() {
    try {
      const response = await this.api.post('/motos/recalcular-km-manutencao');
      return response.data;
    } catch (error) {
      console.error('Erro ao recalcular km manutenção:', error);
      throw error;
    }
  }

  async processarRelatoriosBrasilSat() {
    try {
      const response = await this.api.post('/admin/brasilsat/processar-relatorios');
      return response.data;
    } catch (error) {
      console.error('Erro ao processar relatórios Brasil Sat:', error);
      throw error;
    }
  }

  // ===== EDITOR DE PDF =====
  async uploadPdfMatriz(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await this.api.post('/pdf-editor/upload-matriz', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao fazer upload do PDF matriz:', error);
      throw error;
    }
  }

  async verificarPdfMatriz() {
    try {
      const response = await this.api.get('/pdf-editor/matriz-status');
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar PDF matriz:', error);
      throw error;
    }
  }

  async gerarPdfEditado(dados) {
    try {
      const response = await this.api.post('/pdf-editor/gerar', dados, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      throw error;
    }
  }

  async salvarPosicoesPdf(posicoes) {
    try {
      const response = await this.api.post('/pdf-editor/salvar-posicoes', { posicoes });
      return response.data;
    } catch (error) {
      console.error('Erro ao salvar posições:', error);
      throw error;
    }
  }

  async obterPosicoesPdf() {
    try {
      const response = await this.api.get('/pdf-editor/posicoes');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter posições:', error);
      throw error;
    }
  }

  // ===== BRASIL SAT - DISPOSITIVOS E MAPEAMENTO =====
  async getDispositivosBrasilSat() {
    try {
      const response = await this.api.get('/admin/brasilsat/dispositivos');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter dispositivos Brasil Sat:', error);
      throw error;
    }
  }

  async vincularImeiMoto(motoId, imei) {
    try {
      const response = await this.api.post(`/admin/motos/${motoId}/vincular-imei`, null, {
        params: { imei }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao vincular IMEI:', error);
      throw error;
    }
  }

  async sincronizarGpsAgora() {
    try {
      const response = await this.api.post('/admin/brasilsat/sincronizar-agora');
      return response.data;
    } catch (error) {
      console.error('Erro ao sincronizar GPS:', error);
      throw error;
    }
  }

  async confirmarManutencaoBrasilSat(motoId, dados = {}) {
    try {
      const response = await this.api.post(`/admin/brasilsat/confirmar-manutencao/${motoId}`, dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao confirmar manutenção:', error);
      throw error;
    }
  }

  // ===== MULTI-TENANT - GERENCIAMENTO DE USUÁRIOS =====
  
  async listarUsuariosSistema() {
    try {
      const response = await this.api.get('/usuarios-sistema');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar usuários do sistema:', error);
      throw error;
    }
  }

  async criarUsuarioSistema(dados) {
    try {
      const response = await this.api.post('/usuarios-sistema', dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar usuário do sistema:', error);
      throw error;
    }
  }

  async atualizarUsuarioSistema(usuarioId, dados) {
    try {
      const response = await this.api.put(`/usuarios-sistema/${usuarioId}`, dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar usuário do sistema:', error);
      throw error;
    }
  }

  async deletarUsuarioSistema(usuarioId) {
    try {
      const response = await this.api.delete(`/usuarios-sistema/${usuarioId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao deletar usuário do sistema:', error);
      throw error;
    }
  }

  // PERMISSÕES DE LOCAÇÕES PARA SUB-ADMINS
  async compartilharLocacao(username, locacaoId) {
    try {
      const response = await this.api.post(`/permissoes-subadmin/${username}/adicionar`, {
        locacao_id: locacaoId
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao compartilhar locação:', error);
      throw error;
    }
  }

  async removerCompartilhamentoLocacao(username, locacaoId) {
    try {
      const response = await this.api.delete(`/permissoes-subadmin/${username}/remover/${locacaoId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao remover compartilhamento:', error);
      throw error;
    }
  }

  async listarLocacoesCompartilhadas(username) {
    try {
      const response = await this.api.get(`/permissoes-subadmin/${username}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao listar locações compartilhadas:', error);
      throw error;
    }
  }

  async definirPermissoesLocacoes(username, locacoesIds) {
    try {
      const response = await this.api.put(`/permissoes-subadmin/${username}`, {
        locacoes_permitidas: locacoesIds
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao definir permissões:', error);
      throw error;
    }
  }

  // FILA DE ESPERA
  async cadastrarFilaEspera(dados) {
    const response = await this.api.post('/fila-espera', dados);
    return response.data;
  }

  async listarFilaEspera(status = null) {
    const params = status ? { status } : {};
    const response = await this.api.get('/admin/fila-espera', { params });
    return response.data;
  }

  async atualizarFilaEspera(cadastroId, dados) {
    const response = await this.api.patch(`/admin/fila-espera/${cadastroId}`, dados);
    return response.data;
  }

  async removerFilaEspera(cadastroId) {
    const response = await this.api.delete(`/admin/fila-espera/${cadastroId}`);
    return response.data;
  }

  async contatarFilaEspera(cadastroId) {
    const response = await this.api.post(`/admin/fila-espera/${cadastroId}/contatar`);
    return response.data;
  }

  // ===== DÉBITOS INTEGRADOS COM PAGAMENTOS =====
  async getDebitosPendentesLocacao(locacaoId) {
    const response = await this.api.get(`/locacoes/${locacaoId}/debitos-pendentes`);
    return response.data;
  }

  async confirmarPagamentoComDebitos(pagamentoId, dados) {
    const response = await this.api.put(`/pagamentos/${pagamentoId}/confirmar-pagamento`, dados);
    return response.data;
  }

  async recalcularDebitosPagamentos(locacaoId) {
    const response = await this.api.post(`/locacoes/${locacaoId}/recalcular-debitos`);
    return response.data;
  }

  // ===== ESTOQUE DE PEÇAS =====
  async getEstoque() {
    const response = await this.api.get('/estoque');
    return response.data;
  }

  async getAlertasEstoque() {
    const response = await this.api.get('/estoque/alertas');
    return response.data;
  }

  async addItemEstoque(item) {
    const response = await this.api.post('/estoque', item);
    return response.data;
  }

  async updateItemEstoque(itemId, dados) {
    const response = await this.api.put(`/estoque/${itemId}`, dados);
    return response.data;
  }

  async deleteItemEstoque(itemId) {
    const response = await this.api.delete(`/estoque/${itemId}`);
    return response.data;
  }

  async registrarEntradaEstoque(dados) {
    const response = await this.api.post('/estoque/entrada', dados);
    return response.data;
  }

  async registrarSaidaEstoque(dados) {
    const response = await this.api.post('/estoque/saida', dados);
    return response.data;
  }

  async getTiposManutencaoEstoque() {
    const response = await this.api.get('/estoque/tipos-manutencao');
    return response.data;
  }
}

export const apiService = new ApiService();